import { UserType } from "@/types/IdentityTypes";

/**
 * The two workspaces an account can work in. A landlord account works under
 * `/landlord`; a manager, agent or agency manager under `/agent`. One person
 * can own one property and run another, and a link is often made for the
 * role they hold on a property, not for the type their account has today.
 */
export type Workspace = "/landlord" | "/agent";

/** The account's own workspace, or null for an account with neither. */
export function workspaceOf(userType: unknown): Workspace | null {
  switch (userType) {
    case UserType.Landlord:
      return "/landlord";
    case UserType.Agent:
    case UserType.AgencyManager:
    case UserType.Manager:
      return "/agent";
    default:
      return null;
  }
}

/** Where a link with no twin lands: the workspace's home. */
export const WORKSPACE_HOME: Record<Workspace, string> = {
  "/landlord": "/landlord/home",
  "/agent": "/agent/dashboard",
};

interface Twin {
  /** The page's paths in each workspace; a link lands on the first. */
  "/landlord": string[];
  "/agent": string[];
  /**
   * The page reads one property's records through the rule owners and their
   * delegate managers share (`PropertyAuthorizationService`: a landlord or
   * manager account passes as owner or delegate in either workspace). An agent
   * or agency manager passes only on a property their agency brokers, so a
   * link made for them as an owner would open on a refusal: they go home.
   */
  propertyScoped?: true;
}

const property = (rest = "") => ({
  "/landlord": [`/landlord/properties/:propertyId${rest}`],
  "/agent": [`/agent/properties/:propertyId${rest}`],
  propertyScoped: true as const,
});

const page = (path: string, ...aliases: string[]) => ({
  "/landlord": [`/landlord${path}`, ...aliases.map((a) => `/landlord${a}`)],
  "/agent": [`/agent${path}`, ...aliases.map((a) => `/agent${a}`)],
});

/**
 * Every page under both workspaces with the same meaning and params (App.tsx).
 * Pages left out land on the reader's home: a job's or an inspection's page
 * (each workspace reads only its own seat's jobs and inspections, so the other
 * one's is refused), the owner's signing link (`/landlord/properties/sign/:offerId`,
 * only the landlord workspace signs), adding a property (an owned property in
 * one, a managed one in the other), and the pages only one workspace has
 * (`/agent/ejari`, `/agent/manage-access`).
 */
const TWINS: Twin[] = [
  {
    "/landlord": ["/landlord/home", "/landlord"],
    "/agent": ["/agent/dashboard", "/agent"],
  },
  page("/properties"),
  property(),
  property("/leads"),
  property("/details"),
  property("/money"),
  property("/documents"),
  property("/documents/add"),
  property("/tenancy/add"),
  { ...page("/offers/:offerId"), propertyScoped: true },
  {
    "/landlord": ["/landlord/people"],
    "/agent": ["/agent/people", "/agent/contact"],
  },
  {
    "/landlord": ["/landlord/money", "/landlord/financials"],
    "/agent": ["/agent/money"],
  },
  page("/addenda/:id"),
  page("/chat"),
  page("/profile"),
  page("/billing"),
  page("/billing/success"),
  page("/billing/cancel"),
  page("/jobs"),
  page("/inspections"),
];

/**
 * Pages with no twin whose path a twin's pattern would also take for a
 * property id; like the router, the written segment wins.
 */
const NO_TWIN = ["/landlord/properties/add", "/agent/properties/add"];

const segments = (path: string) => path.split("/").filter(Boolean);

/** The pattern's params from the path, kept as written (still encoded), or null. */
function match(pattern: string, path: string): Record<string, string> | null {
  const want = segments(pattern);
  const got = segments(path);
  if (want.length !== got.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < want.length; i++) {
    const w = want[i]!;
    if (w.startsWith(":")) params[w.slice(1)] = got[i]!;
    else if (w !== got[i]) return null;
  }
  return params;
}

const fill = (pattern: string, params: Record<string, string>) =>
  pattern.replace(/:([A-Za-z]+)/g, (_, name: string) => params[name] ?? "");

/**
 * A link into the workspace the account is not in: the same page in its own
 * workspace, with its params, search and hash, where one exists; else its
 * home. Null when the path is in neither workspace or the account has none,
 * which leaves the caller's own answer.
 */
export function pageInOwnWorkspace(
  location: { pathname: string; search?: string; hash?: string },
  userType: unknown,
): string | null {
  const own = workspaceOf(userType);
  if (!own) return null;
  const other: Workspace = own === "/landlord" ? "/agent" : "/landlord";
  const { pathname, search = "", hash = "" } = location;
  if (pathname !== other && !pathname.startsWith(`${other}/`)) return null;
  if (NO_TWIN.some((pattern) => match(pattern, pathname)))
    return WORKSPACE_HOME[own];
  const sharesPropertyRule =
    userType === UserType.Landlord || userType === UserType.Manager;
  for (const twin of TWINS) {
    if (twin.propertyScoped && !sharesPropertyRule) continue;
    for (const pattern of twin[other]) {
      const params = match(pattern, pathname);
      if (params) return fill(twin[own][0]!, params) + search + hash;
    }
  }
  return WORKSPACE_HOME[own];
}
